import { succeed } from 'jsonous';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { callApi, deleteToApi } from '../../../Appy';
import { currentUserStore } from '../../../CurrentUserStore';
import { findLink } from '../../../LinkyLinky';
import { Link } from '../../../Resource/Types';
import I18nTitle from '../../../Title/I18nTitle';
import { T } from '../../../Translations';
import MainNav from '../../Platform/MainNav';
import withRouter from '../LearningCollection/WithRouter';
import BulkDisableModal from './BulkDisableModal';
import CommunicationsList, { disableableIn, GROUP_LABELS, GroupRun } from './CommunicationsList';
import {
  inheritedTemplateResourceDecoder,
  overridesIndexResourceDecoder,
  previewResourceDecoder,
} from './Decoders';
import Header from './Header';
import OverrideEditor from './OverrideEditor';
import PreviewPane from './PreviewPane';
import {
  CommunicationEntry,
  Draft,
  IndexState,
  OverridesIndexResource,
  PendingAction,
  indexErrored,
  indexLoading,
  indexReady,
  PreviewState,
  previewErrored,
  previewIdle,
  previewLoading,
  previewReady,
  SaveState,
  saved,
  saveErrored,
  saveIdle,
  saving,
  SeedState,
  seedApplied,
  seedIdle,
  seedLoading,
  seedUnavailable,
} from './Types';

interface Props {
  params: { id: string };
}

const substituteCollectionId =
  (id: string) =>
  (link: Link): Link => ({
    ...link,
    href: link.href.replace('{learning_collection_id}', `${id}`),
  });

const draftFor = (entry: CommunicationEntry): Draft => ({
  subject: entry.override.map((o) => o.subject).getOrElseValue(entry.defaultSubject),
  content: entry.override.andThen((o) => o.content).getOrElseValue(''),
  ctaLabel: entry.override.andThen((o) => o.ctaLabel).getOrElseValue(''),
  ctaUrl: entry.override.andThen((o) => o.ctaUrl).getOrElseValue(''),
});

const Communications: React.FC<Props> = ({ params }) => {
  const indexLink = React.useMemo(
    () =>
      findLink('communication-overrides-index')(currentUserStore.links).map(
        substituteCollectionId(params.id),
      ),
    [params.id],
  );
  const previewLink = React.useMemo(
    () =>
      findLink('communication-preview')(currentUserStore.links).map(
        substituteCollectionId(params.id),
      ),
    [params.id],
  );

  const [indexState, setIndexState] = React.useState<IndexState>(indexLoading());
  const [selectedId, setSelectedId] = React.useState<Maybe<number>>(nothing());
  const [draft, setDraft] = React.useState<Draft>({
    subject: '',
    content: '',
    ctaLabel: '',
    ctaUrl: '',
  });
  const [previewState, setPreviewState] = React.useState<PreviewState>(previewIdle());
  const [saveState, setSaveState] = React.useState<SaveState>(saveIdle());
  const [seedState, setSeedState] = React.useState<SeedState>(seedIdle());
  // A section action waits here until it is confirmed. Maybe rather than a
  // boolean plus fields, so "nothing pending" cannot disagree with the detail.
  const [pendingSection, setPendingSection] =
    React.useState<Maybe<{ run: GroupRun; disabled: boolean }>>(nothing());
  // Which action is in flight, so the spinner lands on the row or section being
  // changed instead of the page simply going inert.
  const [pendingAction, setPendingAction] = React.useState<Maybe<PendingAction>>(nothing());

  // Which row the seed request was issued for, so a slow response cannot
  // drop inherited copy into a communication the admin has since moved off.
  const seedingFor = React.useRef<Maybe<number>>(nothing());

  // Opening on the first communication keeps the page from changing shape on
  // the first click. The ref makes this the initial load only, so the refresh
  // after a save or a revert leaves the admin where they were.
  const hasOpenedFirst = React.useRef(false);

  // Everything that happens when a communication becomes the one on screen.
  const openEntry = (entry: CommunicationEntry) => {
    // Claim the seed slot here, for every selection, including ones that will
    // not seed at all. Any inherited-template response still in flight for the
    // previous row now fails the guard in seedFromInherited instead of landing
    // in this draft.
    seedingFor.current = just(entry.communicationId);
    setSelectedId(just(entry.communicationId));
    setDraft(draftFor(entry));
    setSaveState(saveIdle());
    setPreviewState(previewLoading());
    seedFromInherited(entry);
  };

  const fetchIndex = React.useCallback(
    (reopen: Maybe<number> = nothing()) => {
      indexLink.cata({
        Just: (link) => {
          callApi(
            overridesIndexResourceDecoder,
            {},
          )(link).fork(
            () => setIndexState(indexErrored()),
            (resource) => {
              setIndexState(indexReady(resource));

              const communications = resource.payload.communications;
              reopen.cata({
                // Reverting removes the override, so the row has to be reopened
                // against the refreshed entry -- that is what re-seeds it from
                // the inherited message instead of leaving a blank editor.
                Just: (id) => {
                  const entry = communications.find((c) => c.communicationId === id);
                  if (entry) {
                    openEntry(entry);
                  }
                },
                Nothing: () => {
                  const [first] = communications;
                  if (first && !hasOpenedFirst.current) {
                    hasOpenedFirst.current = true;
                    openEntry(first);
                  }
                },
              });
            },
          );
        },
        Nothing: () => setIndexState(indexErrored()),
      });
    },
    // openEntry only ever sets state, and is rebuilt every render; including it
    // here would rebuild fetchIndex too, and the effect below would refetch on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexLink],
  );

  React.useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const selectedEntry: Maybe<CommunicationEntry> =
    indexState.kind === 'ready'
      ? selectedId.andThen((id) => {
          const entry = indexState.resource.payload.communications.find(
            (c) => c.communicationId === id,
          );
          return entry ? just(entry) : nothing();
        })
      : nothing();

  // Only un-overridden communications are seeded. An existing override is the
  // admin's own copy, and replacing it with inherited text would be data loss.
  const seedFromInherited = (entry: CommunicationEntry) => {
    if (entry.override.map(() => true).getOrElseValue(false)) {
      setSeedState(seedIdle());
      return;
    }

    findLink('communication-inherited-template')(entry.links).cata({
      Just: (link) => {
        const seedingId = entry.communicationId;
        const stillSelected = () =>
          seedingFor.current.map((id) => id === seedingId).getOrElseValue(false);

        setSeedState(seedLoading());
        callApi(
          inheritedTemplateResourceDecoder,
          {},
        )(link).fork(
          () => {
            if (stillSelected()) {
              setSeedState(seedUnavailable('unavailable'));
            }
          },
          (resource) => {
            const inherited = resource.payload;
            if (!stillSelected()) {
              return;
            }
            // All four fields have to come from the same tier. The draft
            // subject starts from defaultSubject, which is the root tier, while
            // the seeded body prefers the organization override -- saving that
            // pair would quietly revert an org-customized subject. The payload
            // carries the subject of whichever tier it resolved, so apply it
            // even when no body could be extracted.
            //
            // The button matters as much as the copy: an override composes its
            // HTML from these fields, so prefilling the body while leaving the
            // CTA empty would drop the button from a message that has one.
            setDraft((current) => ({
              ...current,
              subject: inherited.subject.getOrElseValue(current.subject),
              content: inherited.content.getOrElseValue(current.content),
              ctaLabel: inherited.ctaLabel.getOrElseValue(current.ctaLabel),
              ctaUrl: inherited.ctaUrl.getOrElseValue(current.ctaUrl),
            }));

            inherited.content.cata({
              Just: () => setSeedState(seedApplied(inherited.tier)),
              Nothing: () => setSeedState(seedUnavailable(inherited.status)),
            });
          },
        );
      },
      Nothing: () => setSeedState(seedUnavailable('missing')),
    });
  };

  const selectCommunication = (communicationId: number) => {
    if (indexState.kind !== 'ready') {
      return;
    }
    const entry = indexState.resource.payload.communications.find(
      (c) => c.communicationId === communicationId,
    );
    if (entry) {
      openEntry(entry);
    }
  };

  const selectedKey = selectedId.map((id) => `${id}`).getOrElseValue('');

  // Previews are fired on a debounce as the admin types, so responses can
  // return out of order. Only the newest request may paint.
  const previewRequest = React.useRef(0);

  React.useEffect(() => {
    if (selectedKey === '') {
      return;
    }
    const timer = setTimeout(() => {
      previewLink.cata({
        Just: (link) => {
          setPreviewState((current) => (current.kind === 'ready' ? current : previewLoading()));
          previewRequest.current += 1;
          const request = previewRequest.current;
          const latest = () => request === previewRequest.current;
          callApi(previewResourceDecoder, {
            subject: draft.subject,
            content: draft.content,
            cta_label: draft.ctaLabel,
            cta_url: draft.ctaUrl,
          })(link).fork(
            () => {
              if (latest()) {
                setPreviewState(previewErrored());
              }
            },
            (resource) => {
              if (latest()) {
                setPreviewState(previewReady(resource.payload));
              }
            },
          );
        },
        Nothing: () => setPreviewState(previewErrored()),
      });
    }, 700);
    return () => clearTimeout(timer);
  }, [draft.subject, draft.content, draft.ctaLabel, draft.ctaUrl, selectedKey, previewLink]);

  const createLink: Maybe<Link> =
    indexState.kind === 'ready'
      ? findLink('upsert-communication-override')(indexState.resource.links)
      : nothing();

  const save = () => {
    selectedEntry
      .andThen((entry) => createLink.map((link) => ({ entry, link })))
      .cata({
        Just: ({ entry, link }) => {
          setSaveState(saving());
          callApi(succeed({}), {
            communication_id: entry.communicationId,
            subject: draft.subject,
            content: draft.content,
            cta_label: draft.ctaLabel,
            cta_url: draft.ctaUrl,
          })(link).fork(
            () => setSaveState(saveErrored()),
            () => {
              setSaveState(saved());
              // What is on screen is now this collection's own override, so the
              // "started from the inherited message" note no longer holds.
              setSeedState(seedIdle());
              fetchIndex();
            },
          );
        },
        Nothing: () => setSaveState(saveErrored()),
      });
  };

  // Every disable action answers with the refreshed index, so the list,
  // badges and editor all settle from one response.
  const applyIndex = (resource: OverridesIndexResource, reopenId: Maybe<number>) => {
    setSaveState(saveIdle());
    setPendingAction(nothing());
    setIndexState(indexReady(resource));
    reopenId.do((id) => {
      const refreshed = resource.payload.communications.find((c) => c.communicationId === id);
      if (refreshed) {
        openEntry(refreshed);
      }
    });
  };

  const toggleOne: Maybe<(communicationId: number, disabled: boolean) => void> =
    indexState.kind === 'ready'
      ? just((communicationId: number, disabled: boolean) => {
          const entry = indexState.resource.payload.communications.find(
            (c) => c.communicationId === communicationId,
          );
          if (!entry) {
            return;
          }
          findLink('communication-disable')(entry.links).do((link) => {
            setSaveState(saving());
            setPendingAction(just({ kind: 'row', communicationId }));
            callApi(overridesIndexResourceDecoder, { disabled })(link).fork(
              () => {
                setSaveState(saveErrored());
                setPendingAction(nothing());
              },
              (resource) => applyIndex(resource, selectedId),
            );
          });
        })
      : nothing();

  const bulkLink: Maybe<Link> =
    indexState.kind === 'ready'
      ? findLink('communication-disables-bulk')(indexState.resource.links)
      : nothing();

  // Asking first: a section action stops real messages, and the admin should
  // see how many before it happens.
  const toggleSection: Maybe<(run: GroupRun, disabled: boolean) => void> = bulkLink.map(
    () => (run: GroupRun, disabled: boolean) => setPendingSection(just({ run, disabled })),
  );

  const confirmSection = () => {
    pendingSection
      .andThen((pending) => bulkLink.map((link) => ({ pending, link })))
      .do(({ pending, link }) => {
        setSaveState(saving());
        setPendingSection(nothing());
        setPendingAction(just({ kind: 'section', group: pending.run.group }));
        callApi(overridesIndexResourceDecoder, {
          communication_ids: disableableIn(pending.run).map((c) => c.communicationId),
          disabled: pending.disabled,
        })(link).fork(
          () => {
            setSaveState(saveErrored());
            setPendingAction(nothing());
          },
          (resource) => applyIndex(resource, selectedId),
        );
      });
  };

  // Revert any row, not only the selected one, so the menu can offer it
  // wherever a collection override exists.
  const revertOne: Maybe<(communicationId: number) => void> =
    indexState.kind === 'ready'
      ? just((communicationId: number) => {
          const entry = indexState.resource.payload.communications.find(
            (c) => c.communicationId === communicationId,
          );
          if (!entry) {
            return;
          }
          entry.override
            .andThen((override) => findLink('destroy-communication-override')(override.links))
            .do((link) => {
              setSaveState(saving());
              setPendingAction(just({ kind: 'row', communicationId }));
              deleteToApi(link).fork(
                () => {
                  setSaveState(saveErrored());
                  setPendingAction(nothing());
                },
                () => {
                  setSaveState(saveIdle());
                  setPendingAction(nothing());
                  fetchIndex(just(communicationId));
                },
              );
            });
        })
      : nothing();

  const revert: Maybe<() => void> = selectedEntry
    .andThen((entry) =>
      entry.override
        .andThen((override) => findLink('destroy-communication-override')(override.links))
        .map((link) => ({ entry, link })),
    )
    .map(({ entry, link }) => () => {
      setSaveState(saving());
      deleteToApi(link).fork(
        () => setSaveState(saveErrored()),
        () => {
          setSaveState(saveIdle());
          // Reopen this row once the refresh lands: without the override it
          // seeds from the inherited message, which is what "revert to
          // inherited" means. Blanking the draft here would show an empty
          // editor and preview instead.
          fetchIndex(just(entry.communicationId));
        },
      );
    });

  const collectionName =
    indexState.kind === 'ready' ? indexState.resource.payload.collection.name : '';
  const allCommunications =
    indexState.kind === 'ready' ? indexState.resource.payload.communications : [];

  return (
    <div className="lg:h-screen lg:flex lg:flex-col lg:overflow-hidden">
      <I18nTitle title="Collection communications - AdminStudio" />
      <div className="lg:shrink-0">
        <MainNav />
        <Header collectionName={collectionName} communications={allCommunications} />
      </div>
      {/* min-h-0 lets this flex child shrink below its content height, which is
          what allows the columns inside to own the scrolling. */}
      {pendingSection
        .map((pending) => (
          <BulkDisableModal
            isOpen
            sectionLabel={GROUP_LABELS[pending.run.group]}
            affected={disableableIn(pending.run).length}
            withheld={pending.run.communications.length - disableableIn(pending.run).length}
            disabling={pending.disabled}
            onClose={() => setPendingSection(nothing())}
            onConfirm={confirmSection}
          />
        ))
        .getOrElse(() => (
          <></>
        ))}
      <div className="max-w-clientPortalMaxWidth w-full mx-auto px-6 md:px-14 py-6 lg:flex-1 lg:min-h-0">
        {indexState.kind === 'loading' && (
          <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
            <T kind="Loading..." />
          </p>
        )}
        {indexState.kind === 'errored' && (
          <p className="text-destructive" style={{ fontSize: 'var(--text-sm)' }}>
            <T kind="Something went wrong" />
          </p>
        )}
        {indexState.kind === 'ready' && (
          <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_640px] gap-6 items-start lg:items-stretch lg:h-full lg:min-h-0">
            <div className="bg-card border border-border rounded-lg overflow-hidden lg:overflow-y-auto lg:min-h-0">
              <CommunicationsList
                communications={indexState.resource.payload.communications}
                selected={selectedId}
                canManage={createLink.map(() => true).getOrElseValue(false)}
                busy={saveState.kind === 'saving'}
                onSelect={selectCommunication}
                onToggleOne={toggleOne}
                onToggleSection={toggleSection}
                onRevert={revertOne}
                pending={pendingAction}
              />
            </div>
            {selectedEntry
              .map((entry) => (
                <>
                  <div className="bg-card border border-border rounded-lg p-4 lg:overflow-y-auto lg:min-h-0">
                    <OverrideEditor
                      entry={entry}
                      draft={draft}
                      seedState={seedState}
                      canManage={createLink.map(() => true).getOrElseValue(false)}
                      saveState={saveState}
                      onDraftChange={setDraft}
                      onSave={save}
                      onRevert={revert}
                    />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 lg:overflow-y-auto lg:min-h-0">
                    <PreviewPane state={previewState} />
                  </div>
                </>
              ))
              .getOrElse(() => (
                <div className="lg:col-span-2 bg-card border border-border rounded-lg p-8 text-center">
                  <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                    <T kind="Select a communication to customize" />
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(observer(Communications));
